import { BorrowerOpenLoan, MyUser, Security } from '@/modules/common/models';
import { manualLoansStore } from '@/modules/manual-loan/store/store';
import { actions, socketActions } from '@/store/actions';
import { storeGetters as getters } from '@/store/getters';
import { mutations } from '@/store/mutations';
import PermissionValidator from '@/utils/helpers/permissions';
import { ClientConfig, UXConfig, User2FASettings } from '@/utils/helpers/rest';
import { TradingPermission } from '@/utils/helpers/trading-permissions';
import { merge } from 'lodash';
import { VueRouter } from 'vue-router/types/router';
import Vuex, { Store } from 'vuex';

export interface SocketEvents {
  marketplace: {
    orders: { orderRef: string } | null;
  };
}

export interface SocketEvents {
  termLoans: { termContractDisplayId: string } | null;
  openLoans: {
    lenderLoan: { loanId: string } | null;
    borrowerLoan: { loanId: string } | null;
    brokerLoan: { loanId: string } | null;
  };
}

export interface SessionState {
  socketEvents: SocketEvents;
  loginState: LoginState;
  uxConfig: UXConfig;

  borrowerShowAllLoans: boolean;
  borrowerOpenLoans: BorrowerOpenLoan[];

  lastVisitedSymbolOverview: Security | null;

  socketWLToken: string | null;
}

export interface AppState extends SessionState {
  clientConfig: ClientConfig | null;
  frontendHash: string;
  socketConnected: boolean;
  socketReconnecting: boolean;
  sentryConnected: boolean;
}

export interface LoginState {
  user: MyUser | null;
  tfa: User2FASettings | null;
  permissionValidator: PermissionValidator | null;
  tradingPermissions: TradingPermission;
}

const defaultLoginState: LoginState = {
  user: null,
  tfa: null,
  permissionValidator: null,
  tradingPermissions: TradingPermission.DisabledPermissions,
};
const defaultFrontendHash = '';
const defaultUxConfig: UXConfig = {
  forceMarketTimeframesCheckingEvenWhenDisabled: false,
  forceMarketTimeframesClosed: false,
  dismissedPreEstablishedInfo: false,
};
/**
 * Creates a new empty app state for the Store
 */
function newAppState(sessionState: SessionState): AppState {
  return merge(
    {},
    {
      clientConfig: null,
      frontendHash: defaultFrontendHash,
      socketConnected: false,
      socketReconnecting: false,
      sentryConnected: false,
      ...sessionState,
    }
  );
}

/**
 * Creates a new empty Session state for the Store.
 * Session state is a subset of AppState, specific to a user's session
 */
export function newSessionState(initialConfig: Partial<SessionState> = {}): SessionState {
  let uxConfig = {};
  try {
    uxConfig = JSON.parse(sessionStorage.uxConfig);
  } catch {
    // ignore any errors, in that case we just use the default
  }

  return merge<Record<string, never>, SessionState>(
    {},
    {
      socketEvents: {
        marketplace: {
          orders: null,
        },
        termLoans: null,
        openLoans: {
          lenderLoan: null,
          borrowerLoan: null,
          brokerLoan: null,
        },
      },
      loginState: defaultLoginState,
      uxConfig: { ...defaultUxConfig, ...uxConfig },

      borrowerShowAllLoans: false,
      borrowerOpenLoans: [],

      lastVisitedSymbolOverview: null,

      socketWLToken: null,
      ...initialConfig,
    }
  );
}

// lazily initialized singleton by createStore()
export let store: Store<AppState>;

export function createStore(
  _$router: VueRouter,
  initialState: Partial<AppState> = {}
): Store<AppState> {
  const initialConfig = merge({}, initialState);

  return (store = new Vuex.Store<AppState>({
    strict: true,
    state: newAppState(newSessionState(initialConfig)),
    mutations,
    actions: { ...socketActions, ...actions },
    getters,
    modules: {
      manualLoans: manualLoansStore,
    },
  }));
}
